/**
 * PDP "Apple" — Revelação de Fotos. Uma variante por tamanho impresso,
 * pacotes com desconto progressivo (exibição — o desconto real é aplicado
 * no carrinho por uma Shopify Function que lê o mesmo metafield
 * custom.discount_ladder, não por este JS) e a Caixa Cristal como linha de
 * carrinho separada, ligada por variante via custom.box_cristal_variant.
 */

import { resolveInstallments } from './pdp-installments.js';
import { resizeViewportWithKeyboard } from './pdp-apple-viewport.js';
// The group-replace engine (add first, track by _group, keys resolved after
// the add) lives in the old porta-retrato module, where it was extracted
// first — the items builder is injected, so the lines stay this page's own.
// Group tracking matters double here: the discount ladder re-keys the photo
// line on the very add (see pdp-fotos.js).
import { saveGroup } from './pdp-porta-retrato.js';

const money = (cents) =>
  'R$ ' + (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');

const newGroupId = () => `g_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Read one photo-print line back out of a /cart.js payload — the pure half of
 * edit hydration, split from the DOM so it is testable the way readGroup is
 * on the porta-retrato module. Null when the key is gone (the bag can change
 * between opening the editor and saving from it).
 *
 * Size comes from the line's variant (the source of truth) and quantity from
 * the line itself — the 'Quantidade' property only mirrors it.
 */
export function readFotosEditState(cart, editKey) {
  const items = (cart && cart.items) || [];
  const line = items.find((item) => item.key === editKey);
  if (!line) return null;
  const group = (line.properties || {})._group;
  const groupItems = group ? items.filter((item) => (item.properties || {})._group === group) : [line];
  const boxLine = groupItems.find((item) => (item.properties || {})._addon === 'caixa_cristal');
  return {
    keys: groupItems.map((item) => item.key),
    variantId: line.variant_id,
    quantity: line.quantity,
    // '' on a size that never offered the border — the caller only reads it
    // when the hydrated size has one.
    borderLabel: (line.properties || {})['Borda'] || '',
    boxOn: !!boxLine,
    boxColorLabel: boxLine ? (boxLine.properties || {})['Cor da caixa'] || '' : '',
  };
}

export function init(root, cfg) {
  resizeViewportWithKeyboard();
  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || root).querySelectorAll(sel));

  // The header leaves going down and returns going up, as on the other two beta PDPs.
  // No measurement hook: nothing on this page is anchored to --hdr — the preview is
  // static on mobile and pinned at a hard 110px on desktop — so there is no value to
  // keep honest while the header moves. The lock reads the focused field directly,
  // since this page has no editing class of its own: a header sliding against the
  // travel the keyboard triggers is the failure mode the album PDP has paid for.
  const typing = () => {
    const active = document.activeElement;
    return !!active && root.contains(active) && /^(input|textarea)$/i.test(active.tagName);
  };
  // Loaded by the versioned URL from the island: a relative import carries no ?v= and
  // the CDN answers from cache. Fail-open — losing it costs the mechanic, not the page.
  if (cfg.assets?.stickyHeader) {
    import(cfg.assets.stickyHeader)
      .then(({ initStickyHeader }) => {
        initStickyHeader(document.querySelector('.site-header')?.closest('.shopify-section') || null, {
          locked: typing,
        });
      })
      .catch(() => {});
  }

  const optSize = $('#pdpFaOptSize');
  const sizeButtons = $$('.opt', optSize);
  const packsWrap = $('#pdpFaPacks');
  const qtyInput = $('#pdpFaQtyInput');
  const qtyMinus = $('#pdpFaQtyMinus');
  const qtyPlus = $('#pdpFaQtyPlus');
  const qtyTotal = $('#pdpFaQtyTotal');
  const minLabel = $('#pdpFaMinLabel');
  const secBorder = $('#pdpFaSecBorder');
  const borderSeg = $('#pdpFaBorderSeg');
  const secBox = $('#pdpFaSecBox');
  const boxSwitch = $('#pdpFaBoxSwitch');
  const boxFields = $('#pdpFaBoxFields');
  const boxPriceEl = $('#pdpFaBoxPrice');
  // The shared image snippet wraps its <img> in a div, and the asset fallback is a
  // bare <img> — the class lands on whichever is outermost, so reach past it.
  const boxThumbHost = $('#pdpFaAddonBox .addon__thumb');
  const boxThumb = boxThumbHost && boxThumbHost.tagName === 'IMG' ? boxThumbHost : boxThumbHost && boxThumbHost.querySelector('img');
  // What Liquid rendered: the section setting, or the asset. Kept so a size whose
  // box variant has no photo of its own falls back instead of going blank.
  const boxThumbFallback = boxThumb ? boxThumb.getAttribute('src') : null;
  const boxSwatches = $('#pdpFaBoxSwatches');
  const boxColorName = $('#pdpFaBoxColorName');
  const orderBox = $('#pdpFaOrderBox');
  const priceBig = $('#pdpFaPriceBig');
  const priceInst = $('#pdpFaPriceInst');
  const pricePix = $('#pdpFaPricePix');
  const barTotal = $('#pdpFaBarTotal');
  const barSub = $('#pdpFaBarSub');
  const cta = $('#pdpFaCta');
  const galNav = $('#pdpFaGalNav');
  const stage = $('#pdpFaStage');
  const heroTag = root.querySelector('.pdp-fotos-apple-hero .tag');
  const cep = $('#pdpFaCep');
  const cepBtn = $('#pdpFaCepBtn');
  const freight = $('#pdpFaFreight');

  const sizes = sizeButtons.map((b) => ({
    el: b,
    variantId: Number(b.dataset.variantId),
    price: Number(b.dataset.price),
    minQty: Number(b.dataset.minQty),
    boxVariantId: b.dataset.boxVariantId ? Number(b.dataset.boxVariantId) : null,
    boxPrice: b.dataset.boxPrice ? Number(b.dataset.boxPrice) : 0,
    boxImage: b.dataset.boxImage || '',
    showBorder: b.dataset.showBorder === 'true',
  }));

  const state = {
    sizeIndex: 0,
    qty: sizes[0]?.minQty ?? 20,
    // 'border' as the classic's default (pdp-fotos.js): a borda branca é o que
    // sai hoje quando o cliente não mexe no controle.
    border: 'border',
    box: false,
    boxColor: 0,
  };

  if (heroTag) {
    heroTag.textContent = `parcele sem juros ou ${cfg.pixPercent}% off via PIX`;
  }

  function currentSize() {
    return sizes[state.sizeIndex];
  }

  function pick(wrap, btn) {
    $$('.opt', wrap).forEach((el) => el.classList.toggle('on', el === btn));
  }

  optSize?.addEventListener('click', (e) => {
    const b = e.target.closest('.opt');
    if (!b) return;
    const idx = sizes.findIndex((s) => s.el === b);
    if (idx === -1) return;
    state.sizeIndex = idx;
    pick(optSize, b);
    syncBorder();
    syncBox();
    // setQty, nunca `state.qty = …` direto: as regras do novo tamanho aplicam
    // por inteiro E o campo/chips redesenham — cliente não pode ver 20 e pagar 45.
    setQty(state.qty);
  });

  // ── borda branca ──
  // Nem todo tamanho oferece (autocolante nenhum, 5x6 nenhum — a regra vive no
  // Liquid e chega por data-show-border). A escolha do cliente NÃO é zerada ao
  // passar por um tamanho sem borda: quem volta para o 10x15 reencontra o que
  // tinha marcado, e a propriedade só é enviada quando o tamanho tem borda.
  function syncBorder() {
    if (!secBorder) return;
    secBorder.hidden = !currentSize().showBorder;
  }

  // O rótulo escolhido é o que vira o valor da propriedade `Borda` no pedido —
  // um texto só, do resumo ao carrinho, como no clássico.
  const borderLabel = () => (state.border === 'border' ? cfg.borderWith : cfg.borderWithout);

  borderSeg?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    state.border = b.dataset.borderVal;
    $$('button', borderSeg).forEach((el) => el.classList.toggle('on', el === b));
    refresh();
  });

  function syncBox() {
    if (!secBox) return;
    const has = !!currentSize().boxVariantId;
    secBox.hidden = !has;
    if (has) {
      // Imagem lazy dentro de container hidden nunca é buscada, e revelar por
      // JS não dispara o fetch — promover a eager na hora de mostrar.
      secBox.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }
    if (!has) {
      state.box = false;
      boxSwitch?.classList.remove('on');
      $('#pdpFaAddonBox')?.classList.remove('on');
      boxFields?.classList.remove('open');
    } else if (boxPriceEl) {
      boxPriceEl.textContent = '+ ' + money(currentSize().boxPrice);
    }
    // The box is a different object per print size, so the photo follows the size.
    // srcset first: left standing it wins over src and the old photo stays on screen.
    if (boxThumb) {
      const source = currentSize().boxImage || boxThumbFallback;
      if (source && boxThumb.getAttribute('src') !== source) {
        boxThumb.removeAttribute('srcset');
        boxThumb.setAttribute('src', source);
      }
    }
  }

  // ── pacotes + quantidade ──
  function discountPercent(qty) {
    let pct = 0;
    for (const t of cfg.tiers) {
      if (qty >= t.quantity && t.percentage > pct) pct = t.percentage;
    }
    return pct;
  }

  // Math.min(9999, …): max_qty é setting "number" sem teto no schema — 20000
  // no editor não pode furar o campo de 4 dígitos (maxlength do input).
  const maxQty = Math.min(9999, Number(cfg.maxQty) || 9999);

  function setQty(q) {
    const min = currentSize().minQty;
    // `q || min` absorve NaN antes do Math.min; floor depois do teto, que
    // aceita decimal no editor; o mínimo aplica por último e vence — é regra
    // do produto por tamanho, o teto é ajuste operacional.
    state.qty = Math.max(min, Math.floor(Math.min(maxQty, q || min)));
    if (qtyInput) qtyInput.value = state.qty;
    // Sem #pdpFaPacks (produto sem discount_ladder) o guard é obrigatório:
    // `$$(sel, null)` cairia no root e limparia `.on` de controles alheios.
    if (packsWrap) {
      $$('button', packsWrap).forEach((el) => el.classList.toggle('on', Number(el.dataset.q) === state.qty));
    }
    refresh();
  }

  // Safari não desfoca o input no tap de um botão — ler o campo antes de usar
  // state.qty, senão o valor digitado é descartado (mesma cicatriz de pdp-fotos.js).
  const typedQty = () => {
    const v = parseInt(qtyInput?.value, 10);
    return Number.isFinite(v) ? v : state.qty;
  };

  packsWrap?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    setQty(Number(b.dataset.q));
  });
  // Step of 1, like the classic (pdp-fotos.js stepQty): the pack cards cover
  // the big jumps; the stepper exists for fine adjustment.
  qtyMinus?.addEventListener('click', () => setQty(typedQty() - 1));
  qtyPlus?.addEventListener('click', () => setQty(typedQty() + 1));
  qtyInput?.addEventListener('change', (e) => setQty(parseInt(e.target.value, 10)));
  // Preço ao vivo enquanto digita. Sem isto a barra congela no valor anterior
  // até o blur — que no iOS não vem antes do tap no CTA — e o comprador decide
  // vendo um total e paga outro (visto no QA de 14/08: barra R$ 112,50 para o
  // estado antigo de 60 fotos, carrinho R$ 188,00 pelos 100 digitados). O
  // clamp espelha o do setQty, mas SEM reescrever o campo: reescrever aqui
  // brigaria com a digitação ("1" de "100" viraria o mínimo na hora). O
  // change/blur e o ATC (setQty(typedQty())) seguem normalizando o campo.
  qtyInput?.addEventListener('input', (e) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isFinite(v)) return;
    const min = currentSize().minQty;
    state.qty = Math.max(min, Math.floor(Math.min(maxQty, v)));
    if (packsWrap) {
      $$('button', packsWrap).forEach((el) => el.classList.toggle('on', Number(el.dataset.q) === state.qty));
    }
    refresh();
  });

  // ── Caixa Cristal ──
  boxSwitch?.addEventListener('click', () => {
    state.box = !state.box;
    boxSwitch.classList.toggle('on', state.box);
    $('#pdpFaAddonBox')?.classList.toggle('on', state.box);
    boxFields?.classList.toggle('open', state.box);
    refresh();
  });
  boxSwatches?.addEventListener('click', (e) => {
    const b = e.target.closest('.sw');
    if (!b) return;
    const idx = Number(b.dataset.i);
    state.boxColor = idx;
    $$('.sw', boxSwatches).forEach((el, j) => el.classList.toggle('on', j === idx));
    if (boxColorName) boxColorName.textContent = cfg.boxColors[idx]?.label || '';
    refresh();
  });
  if (boxColorName && cfg.boxColors[0]) boxColorName.textContent = cfg.boxColors[0].label;

  // ── galeria ──
  galNav?.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b) return;
    $$('button', galNav).forEach((el) => el.classList.toggle('on', el === b));
    $$('.photo', stage).forEach((p) => p.classList.toggle('on', p.dataset.g === b.dataset.t));
  });


  // ── editing (mobile): the staybar and the WhatsApp float step aside ──
  // The page turns on resizeViewportWithKeyboard, and under interactive-
  // widget=resizes-content Android's layout viewport ends at the keyboard:
  // the bottom:0 staybar and the float rode up over the very field being
  // edited — quantity and CEP (QA 24/08). Same editing class as the sisters
  // (pdp-apple-configurator, pdp-porta-retrato-apple-configurator); the
  // display:none rule lives in the section's mobile @media. On iOS they
  // already sit behind the keyboard — hiding them is a no-op.
  [qtyInput, cep].forEach((input) => {
    input?.addEventListener('focus', () => document.body.classList.add('pdp-fotos-apple-editing'));
    input?.addEventListener('blur', () => document.body.classList.remove('pdp-fotos-apple-editing'));
  });

  // ── acordeões + frete ──
  $$('.acc__head').forEach((h) => {
    h.addEventListener('click', () => {
      root.querySelector('#' + h.dataset.acc)?.classList.toggle('open');
    });
  });
  cep?.addEventListener('input', (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 8);
    if (v.length > 5) v = v.slice(0, 5) + '-' + v.slice(5);
    e.target.value = v;
  });
  cepBtn?.addEventListener('click', () => {
    if (cep && cep.value.replace(/\D/g, '').length === 8) freight?.classList.add('on');
  });

  // ── resumo / total ──
  function refresh() {
    const size = currentSize();
    const pct = discountPercent(state.qty);
    const photosCents = Math.round(state.qty * size.price * (1 - pct / 100));
    const boxCents = state.box ? size.boxPrice : 0;
    const total = photosCents + boxCents;

    if (minLabel) minLabel.textContent = `mín. ${size.minQty} unidades`;
    if (qtyTotal) {
      qtyTotal.innerHTML = '';
      const strong = document.createTextNode(money(photosCents));
      const small = document.createElement('small');
      small.textContent = `${state.qty} × ${money(size.price)}${pct ? ' − ' + pct + '%' : ''}`;
      qtyTotal.append(strong, small);
    }

    if (orderBox) {
      orderBox.innerHTML = '';
      const row = (l, v) => {
        const d = document.createElement('div');
        const l1 = document.createElement('span');
        l1.textContent = l;
        const l2 = document.createElement('span');
        l2.textContent = v;
        d.append(l1, l2);
        orderBox.appendChild(d);
      };
      row('Produto', cfg.product.title);
      row('Tamanho', size.el.querySelector('b')?.textContent || '');
      row('Quantidade', `${state.qty} fotos${pct ? ' · ' + pct + '% off' : ''}`);
      if (size.showBorder) row('Borda', borderLabel());
      if (size.boxVariantId) row(cfg.boxName, state.box ? cfg.boxColors[state.boxColor]?.label || '' : 'Sem caixa');
    }

    if (priceBig) priceBig.textContent = money(total);
    const { count, perInstallmentCents } = resolveInstallments(total, cfg.installmentsRule || { maxCount: 6, minCents: 9000 });
    if (priceInst) priceInst.textContent = count > 1 ? `em até ${count}x de ${money(perInstallmentCents)} sem juros` : 'à vista';
    if (pricePix) pricePix.textContent = `${money(Math.round(total * (1 - (cfg.pixPercent || 0) / 100)))} (${cfg.pixPercent}% de desconto via Pix)`;
    if (barTotal) barTotal.textContent = money(total);

    const sizeLabel = size.el.querySelector('b')?.textContent || '';
    const parts = [`${state.qty}x ${cfg.product.title} ${sizeLabel}`];
    if (state.box) parts.push(`${cfg.boxName} ${cfg.boxColors[state.boxColor]?.label || ''}`);
    if (barSub) barSub.textContent = parts.join(' · ');
  }

  // ── carrinho ──
  let adding = false;
  const addErrorEl = $('#pdpFaAddError');
  const fallbackAddError = cfg.addError || 'Não foi possível adicionar ao carrinho. Tente novamente.';
  const showAddError = (msg) => {
    if (!addErrorEl) return;
    addErrorEl.textContent = msg;
    addErrorEl.hidden = false;
  };

  function buildItems(group) {
    const size = currentSize();
    const sizeLabel = size.el.querySelector('b')?.textContent || '';
    // Mesma chave e mesmos valores do clássico (pdp-fotos.js buildItems): o OMS
    // lê `Borda` do pedido, e um tamanho sem borda não manda a propriedade.
    const photoProps = { _group: group, Tamanho: sizeLabel, Quantidade: String(state.qty) };
    if (size.showBorder) photoProps.Borda = borderLabel();
    const items = [
      {
        id: size.variantId,
        quantity: state.qty,
        properties: photoProps,
      },
    ];
    if (state.box && size.boxVariantId) {
      items.push({
        id: size.boxVariantId,
        quantity: 1,
        properties: { _group: group, _addon: 'caixa_cristal', 'Cor da caixa': cfg.boxColors[state.boxColor]?.label || '' },
      });
    }
    return items;
  }

  // ── edit mode (?edit=<line_key>) ──
  // The drawer's "Editar personalização" link has always pointed here; until
  // the 24/08 QA nothing read the parameter and the page opened at the first
  // size's minimum. A key the cart no longer holds leaves the page as an
  // ordinary buy box, no error.
  const editKey = new URLSearchParams(window.location.search).get('edit');
  let editKeys = null;
  let pendingSave = null;
  // While hydrateEdit is still reading the bag, editKeys is null and a click
  // on the CTA would run a plain add — duplicating the very group the edit is
  // about to replace. The gate holds the CTA until hydration settles, either
  // way it settles.
  let hydrating = !!editKey;
  if (hydrating && cta) cta.disabled = true;
  const editCfg = {
    routes: cfg.routes,
    strings: {
      addError: fallbackAddError,
      saveChanges: cfg.saveChanges,
      lineGone: cfg.lineGone,
      oldCopyLeft: cfg.oldCopyLeft,
    },
  };

  // The DOM half of hydration: readFotosEditState (module scope, tested)
  // decides WHAT the line holds; this only applies it through the same paths
  // the handlers use.
  function applyEditState(saved) {
    editKeys = saved.keys;
    const sizeIdx = sizes.findIndex((sizeOption) => sizeOption.variantId === saved.variantId);
    if (sizeIdx >= 0) {
      state.sizeIndex = sizeIdx;
      pick(optSize, sizes[sizeIdx].el);
      syncBorder();
      syncBox();
    }
    // Só o rótulo "sem borda" derruba o padrão: um valor que não bate com
    // nenhum dos dois (rótulo antigo, renomeado no editor) deixa a borda branca
    // de pé em vez de virar uma terceira coisa.
    if (currentSize().showBorder && saved.borderLabel) {
      state.border = saved.borderLabel === cfg.borderWithout ? 'none' : 'border';
      if (borderSeg) {
        $$('button', borderSeg).forEach((el) => el.classList.toggle('on', el.dataset.borderVal === state.border));
      }
    }
    if (saved.boxOn && currentSize().boxVariantId) {
      state.box = true;
      boxSwitch?.classList.add('on');
      $('#pdpFaAddonBox')?.classList.add('on');
      boxFields?.classList.add('open');
      const colorIdx = (cfg.boxColors || []).findIndex((colorOption) => colorOption.label === saved.boxColorLabel);
      if (colorIdx >= 0) {
        state.boxColor = colorIdx;
        $$('.sw', boxSwatches).forEach((swatchEl, swatchIndex) => swatchEl.classList.toggle('on', swatchIndex === colorIdx));
        if (boxColorName) boxColorName.textContent = cfg.boxColors[colorIdx].label;
      }
    }
    setQty(saved.quantity);
    if (cta && cfg.saveChanges) cta.textContent = cfg.saveChanges;
  }

  async function hydrateEdit() {
    if (!editKey || !cfg.routes.cart) {
      hydrating = false;
      if (cta) cta.disabled = false;
      return;
    }
    try {
      const res = await fetch(cfg.routes.cart, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const saved = readFotosEditState(cart, editKey);
      if (!saved) return;
      applyEditState(saved);
    } catch {
      // graceful fallback: ordinary buy box
    } finally {
      hydrating = false;
      if (cta) cta.disabled = false;
    }
  }

  cta?.addEventListener('click', async () => {
    if (adding || hydrating) return;
    adding = true;
    cta.disabled = true;
    if (addErrorEl) addErrorEl.hidden = true;
    // Última chance de capturar um valor digitado que o blur nunca viu
    // (Safari não desfoca no tap do botão).
    setQty(typedQty());
    try {
      if (editKeys) {
        // In edit mode the whole group is replaced (add first, atomic removal
        // after) — never a second line.
        const sections = await saveGroup({
          config: editCfg,
          state: {},
          editKey,
          fallbackKeys: editKeys,
          sectionsUrl: window.location.pathname,
          pending: pendingSave,
          itemsFor: (group) => buildItems(group),
        });
        pendingSave = null;
        document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
        return;
      }
      const res = await fetch(cfg.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: buildItems(newGroupId()), sections: 'cart-drawer', sections_url: window.location.pathname }),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        // resposta não-JSON (página de erro) — o res.ok abaixo decide
      }
      if (!res.ok) {
        showAddError((data && (data.description || data.message)) || fallbackAddError);
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: data.sections } }));
    } catch (error) {
      // A partial save records what it already did (pending) so a retry
      // finishes the removal instead of adding a third copy; sections riding
      // on the error reopen the drawer so the old copy the message names is
      // actually visible.
      if (error && error.pending) {
        pendingSave = error.pending;
        if (error.pending.sections) {
          document.dispatchEvent(new CustomEvent('cart:open', { detail: { sections: error.pending.sections } }));
        }
      }
      showAddError((error && error.message) || fallbackAddError);
    } finally {
      adding = false;
      cta.disabled = false;
    }
  });

  // Publica a altura da staybar para o CSS afastar o float do WhatsApp do CTA
  // (cresce quando o erro aparece; ResizeObserver acompanha).
  const staybar = root.querySelector('.staybar');
  if (staybar && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--pdp-staybar-h', `${staybar.offsetHeight}px`);
    }).observe(staybar);
  }

  syncBorder();
  syncBox();
  setQty(currentSize().minQty);
  hydrateEdit();
}
